// src/components/IcalImportBtn.jsx — bouton 1-clic d'import iCal pour un
// logement et un mois donnés. Affiche le nombre d'ajouts ou "Déjà à jour" 3s
// après l'appel. Utilisé dans la sidebar Données et l'onglet Accueil.
//
// Fix B-2 (2026-05-23) : re-fetch blob frais depuis storage AVANT d'appeler
// importIcalForLog. Sans cela, le `data` prop React pourrait être stale (un
// tombstone posé entre rendu et clic serait absent) → réinsertion indésirable.
// Pattern identique à refreshAllIcal (App.jsx) ADR-077.
//
// F13 (2026-10-02) : la relecture passe par /api/data (`fetchData`), qui rend
// les données ET leur jeton. L'écriture de l'import porte CE jeton — celui des
// données dont elle part — et s'arrête si un enregistrement de Dimitri passe
// pendant l'import (avant : lecture directe sans jeton, écriture avec le jeton
// détenu, donc par-dessus un enregistrement fait entre-temps).

import { useState } from "react"
import { fetchData, jetonDetenu, preparerEcrituresImport } from "../lib/data-api"
import { DS } from "../lib/ui"
import { importIcalForLog } from "../lib/channels/ical"
import { migrateData } from "../lib/data-migration"

export default function IcalImportBtn({ log, month, y, m, data, saveData, onChangementsAilleurs }) {
  const [loading, setLoading] = useState(false)
  const [msg, setMsg] = useState("")

  const importIcal = async () => {
    setLoading(true); setMsg("")
    try {
      // B-2 : lire le blob frais (le prop `data` peut être stale).
      let freshData = null
      let jetonLu = ""
      // Noté AVANT la relecture : un enregistrement qui aboutit pendant
      // qu'on relit rend ce qu'on a lu douteux — on n'importe pas.
      const jetonAvant = jetonDetenu()
      try {
        const lu = await fetchData()
        // `{}` = base vide : même abandon qu'une lecture ratée (rien à
        // compléter, et l'import écrirait un blob partiel).
        freshData = lu.data && Object.keys(lu.data).length > 0 ? migrateData(lu.data) : null
        jetonLu = lu.jeton
      } catch (e) {
        console.error('[IcalImportBtn] lecture des données échouée:', e)
      }
      if (!freshData || jetonDetenu() !== jetonAvant) {
        // Lecture indisponible, ou un enregistrement est passé pendant la
        // lecture → abort au lieu de réutiliser data prop stale.
        setMsg("Erreur")
      } else {
        const ecritures = preparerEcrituresImport({
          jetonLu,
          montrerBandeau: (msg) => { if (onChangementsAilleurs) onChangementsAilleurs(msg) },
        })
        const added = await importIcalForLog(log, month, y, m, freshData, async (nd) => {
          const jeton = ecritures.jetonPourEcrire()
          if (jeton === null) return false
          const ok = await saveData(nd, { jeton, surConflit: ecritures.conflit })
          ecritures.apresEcriture(ok)
          return ok
        })
        // Une écriture arrêtée ou refusée n'est jamais annoncée comme un ajout.
        if (ecritures.arrete()) setMsg("Erreur")
        else setMsg(added > 0 ? `${added} ajout${added > 1 ? "s" : ""}!` : "Déjà à jour")
      }
    } catch (e) {
      setMsg("Erreur")
    }
    setLoading(false)
    setTimeout(() => setMsg(""), 3000)
  }

  return (
    <div style={{display:"flex",alignItems:"center",gap:4}}>
      <button onClick={importIcal} disabled={loading}
        style={{background:loading?"#aaa":"#e8f0ff",color:DS.navyLight,border:"none",borderRadius:7,padding:"6px 10px",fontSize:11,fontWeight:700,cursor:loading?"wait":"pointer",fontFamily:DS.fontBody,whiteSpace:"nowrap"}}>
        {loading ? "..." : "📅 iCal"}
      </button>
      {msg && <span style={{fontSize:10,color:msg.includes("Erreur")?DS.red:DS.green,fontWeight:700}}>{msg}</span>}
    </div>
  )
}
